// src/pages/tender/ContractorDashboard.js

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import toast, { Toaster } from "react-hot-toast";
import { db } from "../../firebaseConfig";
import ContractorConstructionPanel from "./ContractorConstructionPanel";

const GST_RATE = 18;

const formatINR = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const toDate = (value) => {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const fmtDate = (value) => {
  try {
    return toDate(value)?.toLocaleDateString("en-IN") || "—";
  } catch {
    return "—";
  }
};

const SECTION_INFO = {
  projects: {
    title: "Awarded Projects",
    description: "View LOA, work order, project period and complete award details.",
    button: "Open Project",
    path: "/tender/loa",
    icon: "projects",
  },
  progress: {
    title: "Daily Progress",
    description: "Submit and review project-wise daily site progress records.",
    button: "Open Progress",
    path: "/tender/progress",
    icon: "progress",
  },
  construction: {
    title: "Construction Progress",
    description: "Manage stage-wise construction progress and supporting evidence.",
    button: "Open Construction",
    path: "/tender/dashboard?section=construction",
    icon: "construction",
  },
  measurement: {
    title: "Measurement Sheet",
    description: "Project-wise measurement, RA, GST, paid amount and outstanding status.",
    button: "Open Measurement",
    path: "/tender/measurement",
    icon: "measurement",
  },
  ra: {
    title: "RA Bills & GST",
    description: "Track RA bills, approvals and upload the applicable GST invoice.",
    button: "Open RA Bills",
    path: "/tender/ra",
    icon: "ra",
  },
  payments: {
    title: "Payments",
    description: "Review payment vouchers, UTR details and outstanding balances.",
    button: "View Payments",
    path: "/tender/payments",
    icon: "payments",
  },
  documents: {
    title: "My Documents",
    description: "Access contractor and project-related documents in one place.",
    button: "Open Documents",
    path: "/tender/documents",
    icon: "documents",
  },
};

const DASHBOARD_MODULES = [
  { key: "projects", ...SECTION_INFO.projects },
  { key: "progress", ...SECTION_INFO.progress },
  { key: "construction", ...SECTION_INFO.construction },
  { key: "measurement", ...SECTION_INFO.measurement },
  { key: "ra", ...SECTION_INFO.ra },
  { key: "payments", ...SECTION_INFO.payments },
  { key: "documents", ...SECTION_INFO.documents },
  {
    key: "profile",
    title: "Company Profile",
    description: "Review company, contact and registration information.",
    path: "/tender/profile",
    icon: "profile",
    direct: true,
  },
  {
    key: "settings",
    title: "Account Settings",
    description: "Manage contractor portal preferences and account settings.",
    path: "/tender/settings",
    icon: "settings",
    direct: true,
  },
];

function ModuleIcon({ name, className = "h-6 w-6" }) {
  const props = {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const paths = {
    projects: (
      <>
        <path d="M4 20V8l8-4 8 4v12" />
        <path d="M8 20v-6h8v6M3 20h18" />
      </>
    ),
    progress: <path d="M4 19V9M10 19V5M16 19v-7M22 19H2" />,
    construction: <path d="m4 19 8-14 8 14M7 14h10M9 10h6M3 19h18" />,
    measurement: <path d="M4 5h16v14H4zM8 5v4m4-4v4m4-4v4M7 14h10" />,
    ra: <path d="M6 3h9l3 3v15H6zM14 3v4h4M9 12h6M9 16h6" />,
    payments: (
      <>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18M7 15h3" />
      </>
    ),
    documents: <path d="M6 3h9l3 3v15H6zM14 3v4h4M9 12h6M9 16h4" />,
    profile: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
      </>
    ),
  };

  return <svg {...props}>{paths[name] || paths.projects}</svg>;
}

function Badge({ type, children }) {
  const styles = {
    green: "border-emerald-300 bg-emerald-50 text-emerald-800",
    blue: "border-blue-300 bg-blue-50 text-blue-800",
    yellow: "border-amber-300 bg-amber-50 text-amber-800",
    red: "border-red-300 bg-red-50 text-red-700",
    gray: "border-slate-300 bg-slate-50 text-slate-700",
  };

  return (
    <span
      className={`inline-flex items-center justify-center rounded-md border px-2 py-1 text-[9px] font-extrabold leading-none ${
        styles[type] || styles.gray
      }`}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value, note, compact = false }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
      <div className="absolute inset-y-0 left-0 w-1 bg-emerald-600" />
      <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
        {label}
      </div>
      <div
        className={`${
          compact ? "text-lg" : "text-xl 2xl:text-2xl"
        } mt-1 break-words font-extrabold leading-tight text-slate-950`}
      >
        {value}
      </div>
      <div className="mt-1 text-[10px] font-medium text-slate-400">{note}</div>
    </div>
  );
}

function DashboardModuleCard({ module, value, note, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group min-h-[176px] w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-[0_6px_22px_rgba(15,23,42,0.05)] transition hover:-translate-y-0.5 hover:border-emerald-400 hover:shadow-[0_12px_28px_rgba(15,23,42,0.09)] focus:outline-none focus:ring-2 focus:ring-emerald-500"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-white transition group-hover:bg-emerald-700">
          <ModuleIcon name={module.icon} />
        </span>
        {value !== undefined && (
          <span className="max-w-[58%] break-words rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1 text-right text-sm font-extrabold text-emerald-800">
            {value}
          </span>
        )}
      </div>
      <h3 className="mt-5 text-[15px] font-extrabold text-slate-950">{module.title}</h3>
      <p className="mt-1.5 min-h-[40px] text-[11px] font-medium leading-5 text-slate-500">
        {module.description}
      </p>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-[10px] font-extrabold uppercase tracking-[0.08em] text-emerald-700">
        <span>{note || "Open Module"}</span>
        <span aria-hidden="true">→</span>
      </div>
    </button>
  );
}

function ProgressBar({ value }) {
  const percentage = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <div className="min-w-0">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full rounded-full bg-emerald-600"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="mt-1 text-center text-[9px] font-extrabold text-slate-700">
        {percentage}%
      </div>
    </div>
  );
}

function getCountdown(row) {
  const start = toDate(row.workStartDate);
  if (!start || !row.workDurationDays) {
    return { daysLeft: "—", label: "Not Set", color: "text-slate-500" };
  }

  const today = new Date();
  start.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const passed = Math.floor((today - start) / 86400000);
  const left = Number(row.workDurationDays) - passed;

  if (left < 0) {
    return { daysLeft: Math.abs(left), label: "Overdue", color: "text-red-600" };
  }
  if (left <= 7) {
    return { daysLeft: left, label: "Urgent", color: "text-amber-600" };
  }
  return { daysLeft: left, label: "On Time", color: "text-emerald-700" };
}

function getDelayPenalty(row) {
  const start = toDate(row.workStartDate);
  if (!start || !row.workDurationDays) return { overdueDays: 0, penalty: 0 };

  const today = new Date();
  start.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const passedDays = Math.floor((today - start) / 86400000);
  const overdueDays = passedDays - Number(row.workDurationDays || 0);
  if (overdueDays <= 0) return { overdueDays: 0, penalty: 0 };

  const awardAmount = Number(row.awardAmount || 0);
  const fullWeeks = Math.floor(overdueDays / 7);
  const penalty = Math.min(awardAmount * (fullWeeks * 0.01), awardAmount * 0.05);

  return { overdueDays, penalty };
}

function getPaymentStatus(netPayable, paidAmount, rawStatus) {
  const net = Number(netPayable || 0);
  const paid = Number(paidAmount || 0);

  if (paid > 0 && paid >= net) return "FULLY_PAID";
  if (paid > 0 && paid < net) return "PART_PAID";
  return rawStatus || "PENDING";
}

function paymentBadgeType(status) {
  const value = String(status || "").toUpperCase();
  if (value === "FULLY_PAID" || value === "PAYMENT_VERIFIED") return "green";
  if (value === "PART_PAID") return "blue";
  if (value === "PENDING") return "yellow";
  return "red";
}

function ProjectMobileCard({ project, index, sectionInfo, onViewRa, onOpen }) {
  const countdown = getCountdown(project);
  const delayPenalty = getDelayPenalty(project);
  const status =
    project.progress >= 95
      ? "Completed"
      : project.progress > 0
      ? "In Progress"
      : "Not Started";
  const badgeType = project.progress >= 95 ? "green" : project.progress > 0 ? "blue" : "gray";
  const paymentStatus =
    project.paymentOutstandingTotal <= 0 && project.paymentPaidTotal > 0
      ? "FULLY_PAID"
      : project.paymentPaidTotal > 0
      ? "PART_PAID"
      : "PENDING";

  const fields = [
    ["Award Value", formatINR(project.awardAmount)],
    ["RA Basic", formatINR(project.raTotal)],
    ["Approved RA", formatINR(project.raApprovedTotal)],
    ["Paid", formatINR(project.paymentPaidTotal)],
    ["Outstanding", formatINR(project.paymentOutstandingTotal)],
    ["GST Invoice", `${project.gstInvoiceUploaded} uploaded / ${project.gstInvoicePending} pending`],
  ];

  return (
    <article className="min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="min-w-0">
          <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">
            Project {index + 1}
          </div>
          <h3 className="mt-1 break-words text-sm font-extrabold text-slate-950">
            {project.projectTitle}
          </h3>
          <p className="mt-1 break-all font-mono text-[9px] text-slate-500">ID: {project.id}</p>
        </div>
        <Badge type={badgeType}>{status}</Badge>
      </div>

      <div className="grid grid-cols-2 border-b border-slate-200 text-[11px]">
        <div className="border-r border-slate-200 p-3">
          <div className="font-bold text-slate-400">PACS / Society</div>
          <div className="mt-1 font-extrabold text-slate-800">{project.pacsName}</div>
        </div>
        <div className="p-3">
          <div className="font-bold text-slate-400">District</div>
          <div className="mt-1 font-extrabold text-slate-800">{project.district}</div>
        </div>
      </div>

      <div className="border-b border-slate-200 p-4">
        <div className="flex items-center justify-between text-[10px] font-extrabold text-slate-500">
          <span>CONSTRUCTION PROGRESS</span>
          <span>{project.progress}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-emerald-600"
            style={{ width: `${Math.max(0, Math.min(100, project.progress))}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 text-[10px]">
        {fields.map(([label, value], fieldIndex) => (
          <div
            key={label}
            className={`border-b border-slate-200 p-3 ${fieldIndex % 2 === 0 ? "border-r" : ""}`}
          >
            <div className="font-bold uppercase tracking-wide text-slate-400">{label}</div>
            <div className="mt-1 break-words font-extrabold text-slate-800">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 border-b border-slate-200 bg-slate-50 text-center text-[10px]">
        <div className="border-r border-slate-200 p-3">
          <div className="font-bold text-slate-400">Duration</div>
          <div className="mt-1 font-extrabold text-slate-800">
            {project.workDurationDays ? `${project.workDurationDays} Days` : "—"}
          </div>
        </div>
        <div className="border-r border-slate-200 p-3">
          <div className="font-bold text-slate-400">Days Left</div>
          <div className={`mt-1 font-extrabold ${countdown.color}`}>
            {countdown.daysLeft === "—" ? "—" : countdown.daysLeft} · {countdown.label}
          </div>
        </div>
        <div className="p-3">
          <div className="font-bold text-slate-400">Payment</div>
          <div className="mt-1">
            <Badge type={paymentBadgeType(paymentStatus)}>{paymentStatus}</Badge>
          </div>
        </div>
      </div>

      {delayPenalty.penalty > 0 && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-[10px] font-bold text-red-700">
          Delay penalty: {formatINR(delayPenalty.penalty)} ({delayPenalty.overdueDays} days delay)
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 p-4">
        <button
          type="button"
          onClick={() => onViewRa(project)}
          className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-[10px] font-extrabold text-blue-800 hover:bg-blue-100"
        >
          View RA / GST
        </button>
        <button
          type="button"
          onClick={() => onOpen(project)}
          className="rounded-lg bg-emerald-700 px-3 py-2.5 text-[10px] font-extrabold text-white hover:bg-emerald-800"
        >
          {sectionInfo.button}
        </button>
      </div>
    </article>
  );
}

export default function ContractorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const storage = getStorage();

  const requestedSection = new URLSearchParams(location.search).get("section") || "home";
  const section =
    requestedSection === "home" || SECTION_INFO[requestedSection]
      ? requestedSection
      : "home";
  const sectionInfo = SECTION_INFO[section] || null;

  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRaProject, setSelectedRaProject] = useState(null);
  const [uploadingRaId, setUploadingRaId] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        toast.error("Please login first.");
        navigate("/tender/login", { replace: true });
        return;
      }
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const vendorSnapshot = await getDoc(doc(db, "vendors", user.uid));
      setVendor(
        vendorSnapshot.exists()
          ? { id: vendorSnapshot.id, ...vendorSnapshot.data() }
          : null
      );

      const tenderSnapshot = await getDocs(
        query(collection(db, "tenders"), where("awardedVendorUid", "==", user.uid))
      );

      const rows = [];

      for (const tenderDocument of tenderSnapshot.docs) {
        const tender = tenderDocument.data();
        const tenderId = tenderDocument.id;

        let raCount = 0;
        let raPending = 0;
        let raApproved = 0;
        let raTotal = 0;
        let raApprovedTotal = 0;
        let gstInvoicePending = 0;
        let gstInvoiceUploaded = 0;
        let paymentPaidTotal = 0;
        let paymentOutstandingTotal = 0;
        let fullyPaidCount = 0;
        let partPaidCount = 0;
        let paymentPendingCount = 0;
        const raBillList = [];

        try {
          const raSnapshot = await getDocs(
            query(
              collection(db, "ra_bills"),
              where("tenderId", "==", tenderId),
              where("vendorUid", "==", user.uid)
            )
          );

          raSnapshot.forEach((raDocument) => {
            const rawBill = { id: raDocument.id, ...raDocument.data() };
            const basic = Number(
              rawBill.basicAmount || rawBill.totalAmount || rawBill.grossAmount || 0
            );
            const gst = Number(
              rawBill.gstAmount || Math.round((basic * GST_RATE) / 100)
            );
            const grossWithGST = Number(rawBill.grossWithGST || basic + gst);
            const penalty = Number(rawBill.penaltyAmount || 0);
            const deduction = Number(rawBill.deductionAmount || 0);
            const tds = Number(rawBill.tdsAmount || 0);
            const retention = Number(rawBill.retentionAmount || 0);
            const netPayable = Number(
              rawBill.netPayable ||
                grossWithGST - penalty - deduction - tds - retention
            );
            const paidAmount = Number(
              rawBill.totalPaidAmount ||
                rawBill.allocatedPaidAmount ||
                rawBill.paymentAmount ||
                0
            );
            const outstandingAmount =
              rawBill.outstandingAmount !== undefined
                ? Number(rawBill.outstandingAmount || 0)
                : Math.max(netPayable - paidAmount, 0);
            const paymentStatus = getPaymentStatus(
              netPayable,
              paidAmount,
              rawBill.paymentStatus
            );

            raCount += 1;
            raTotal += basic;

            if (rawBill.gstInvoiceUrl) gstInvoiceUploaded += 1;
            else gstInvoicePending += 1;

            const isApproved =
              rawBill.accountingStatus === "CEO_APPROVED" ||
              String(rawBill.status || "").toLowerCase() === "approved";

            if (isApproved) {
              raApproved += 1;
              raApprovedTotal += netPayable;
              paymentPaidTotal += paidAmount;
              paymentOutstandingTotal += outstandingAmount;

              if (
                paymentStatus === "FULLY_PAID" ||
                paymentStatus === "PAYMENT_VERIFIED"
              ) {
                fullyPaidCount += 1;
              } else if (paymentStatus === "PART_PAID") {
                partPaidCount += 1;
              } else {
                paymentPendingCount += 1;
              }
            } else {
              raPending += 1;
            }

            raBillList.push({
              ...rawBill,
              basicAmount: basic,
              gstAmount: gst,
              grossWithGST,
              netPayable,
              paidAmount,
              outstandingAmount,
              paymentStatus,
              lastPaymentVoucherNo: rawBill.lastPaymentVoucherNo || "",
              lastPaymentUtr:
                rawBill.lastPaymentUtr || rawBill.paymentUtr || "",
              lastPaymentDate:
                rawBill.lastPaymentDate || rawBill.paymentDate || "",
              penaltyAmount: penalty,
              deductionAmount: deduction,
              tdsAmount: tds,
              retentionAmount: retention,
            });
          });
        } catch (error) {
          console.warn("RA read failed", tenderId, error);
        }

        rows.push({
          id: tenderId,
          projectTitle: tender.title || tender.projectName || "Project",
          pacsName:
            tender.ownerCompany || tender.pacsName || tender.organization || "—",
          district: tender.city || tender.district || "—",
          state: tender.state || "Gujarat",
          capacityMt: tender.capacityMt || "—",
          awardAmount:
            tender.awardedAmount ||
            tender.tenderAwardAmount ||
            tender.awardedValue ||
            tender.amount ||
            0,
          progress: Number(tender.constructionOverallProgress || 0),
          loaPdfUrl: tender.loaPdfUrl || tender.workOrderPdfUrl || "",
          signedLoaUrl: tender.signedLoaUrl || "",
          loaStatus: tender.signedLoaUrl
            ? "Submitted"
            : tender.loaStatus || "Pending",
          loaGenerateDate: tender.loaGenerateDate || "",
          workStartDate: tender.workStartDate || "",
          workDurationDays: tender.workDurationDays || 0,
          raCount,
          raPending,
          raApproved,
          raTotal,
          raApprovedTotal,
          gstInvoicePending,
          gstInvoiceUploaded,
          paymentPaidTotal,
          paymentOutstandingTotal,
          fullyPaidCount,
          partPaidCount,
          paymentPendingCount,
          raBillList,
        });
      }

      setProjects(rows);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load contractor dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filteredProjects = useMemo(() => {
    let list = [...projects];

    if (statusFilter === "notStarted") list = list.filter((item) => item.progress <= 0);
    if (statusFilter === "inProgress") {
      list = list.filter((item) => item.progress > 0 && item.progress < 95);
    }
    if (statusFilter === "completed") list = list.filter((item) => item.progress >= 95);
    if (statusFilter === "raPending") list = list.filter((item) => item.raPending > 0);
    if (statusFilter === "gstPending") {
      list = list.filter((item) => item.gstInvoicePending > 0);
    }
    if (statusFilter === "paymentPending") {
      list = list.filter((item) => item.paymentOutstandingTotal > 0);
    }
    if (statusFilter === "partPaid") list = list.filter((item) => item.partPaidCount > 0);
    if (statusFilter === "fullyPaid") list = list.filter((item) => item.fullyPaidCount > 0);

    const search = searchText.trim().toLowerCase();
    if (search) {
      list = list.filter((item) =>
        [item.projectTitle, item.pacsName, item.district, item.state, item.id]
          .join(" ")
          .toLowerCase()
          .includes(search)
      );
    }

    return list;
  }, [projects, searchText, statusFilter]);

  const totals = useMemo(() => {
    const total = projects.length;
    const average = total
      ? Math.round(
          projects.reduce((sum, item) => sum + Number(item.progress || 0), 0) /
            total
        )
      : 0;

    return {
      total,
      avg: average,
      pendingRa: projects.reduce((sum, item) => sum + item.raPending, 0),
      approvedRa: projects.reduce(
        (sum, item) => sum + item.raApprovedTotal,
        0
      ),
      gstPending: projects.reduce(
        (sum, item) => sum + item.gstInvoicePending,
        0
      ),
      gstUploaded: projects.reduce(
        (sum, item) => sum + item.gstInvoiceUploaded,
        0
      ),
      paidAmount: projects.reduce(
        (sum, item) => sum + item.paymentPaidTotal,
        0
      ),
      outstandingAmount: projects.reduce(
        (sum, item) => sum + item.paymentOutstandingTotal,
        0
      ),
    };
  }, [projects]);

  const doLogout = async () => {
    await signOut(auth);
    navigate("/tender/login", { replace: true });
  };

  const openModule = (module) => {
    if (module.direct) {
      navigate(module.path);
      return;
    }

    navigate(`${location.pathname}?section=${module.key}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToDashboard = () => {
    navigate(`${location.pathname}?section=home`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openProject = (project) => {
    if (!sectionInfo?.path) return;
    navigate(`${sectionInfo.path}/${project.id}`);
  };

  const uploadGstInvoice = async (raBill, file) => {
    if (!file) return;

    try {
      setUploadingRaId(raBill.id);

      const filePath = `ra_bill_invoices/${raBill.tenderId}/${user.uid}/${
        raBill.id
      }_${Date.now()}_${file.name}`;
      const fileReference = ref(storage, filePath);
      await uploadBytes(fileReference, file);
      const url = await getDownloadURL(fileReference);

      await updateDoc(doc(db, "ra_bills", raBill.id), {
        gstInvoiceUrl: url,
        gstInvoicePath: filePath,
        gstInvoiceName: file.name,
        gstInvoiceUploadedAt: serverTimestamp(),
        gstInvoiceUploadedBy: user.uid,
        accountingStatus:
          raBill.accountingStatus === "CORRECTION_REQUIRED" ||
          raBill.accountingStatus === "CEO_REJECTED_CORRECTION"
            ? "GST_INVOICE_RESUBMITTED"
            : raBill.accountingStatus || "GST_INVOICE_UPLOADED",
        updatedAt: serverTimestamp(),
      });

      toast.success("GST invoice uploaded successfully");
      setSelectedRaProject(null);
      loadData();
    } catch (error) {
      console.error(error);
      toast.error("GST invoice upload failed");
    } finally {
      setUploadingRaId("");
    }
  };

  const vendorProfile = vendor?.profile || {};
  const companyName = vendorProfile.companyName || "Contractor Company";
  const vendorEmail = vendor?.auth?.email || user?.email || "";
  const vendorId = vendor?.vendorId || vendor?.id || "";

  const getModuleMetric = (moduleKey) => {
    const metrics = {
      projects: { value: totals.total, note: "Projects Assigned" },
      progress: { value: `${totals.avg}%`, note: "Average Progress" },
      construction: { value: `${totals.avg}%`, note: "Stage Tracking" },
      measurement: { value: totals.total, note: "Project Records" },
      ra: { value: totals.pendingRa, note: `${totals.gstPending} GST Pending` },
      payments: {
        value: formatINR(totals.outstandingAmount),
        note: "Outstanding Balance",
      },
      documents: { value: totals.gstUploaded, note: "GST Files Uploaded" },
    };

    return metrics[moduleKey] || {};
  };

  const renderStats = (compact = false) => (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 2xl:grid-cols-8">
      <StatCard label="Awarded Projects" value={totals.total} note="Projects assigned" compact={compact} />
      <StatCard label="Average Progress" value={`${totals.avg}%`} note="Across projects" compact={compact} />
      <StatCard label="Pending RA" value={totals.pendingRa} note="Waiting approval" compact={compact} />
      <StatCard label="Approved RA" value={formatINR(totals.approvedRa)} note="CEO approved" compact={compact} />
      <StatCard label="Paid Amount" value={formatINR(totals.paidAmount)} note="Payment received" compact={compact} />
      <StatCard label="Outstanding" value={formatINR(totals.outstandingAmount)} note="Payment balance" compact={compact} />
      <StatCard label="GST Pending" value={totals.gstPending} note="Upload required" compact={compact} />
      <StatCard label="GST Uploaded" value={totals.gstUploaded} note="Submitted invoices" compact={compact} />
    </div>
  );

  const renderDesktopProjectTable = () => (
    <div className="hidden 2xl:block">
      <table className="w-full table-fixed border-collapse text-[8.5px] text-slate-700">
        <colgroup>
          <col style={{ width: "2%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "4%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "4%" }} />
          <col style={{ width: "4%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "4%" }} />
          <col style={{ width: "8%" }} />
        </colgroup>
        <thead>
          <tr className="bg-slate-900 text-white">
            {[
              "Sr",
              "Project Name",
              "PACS / Society",
              "District",
              "Progress",
              "Award Value",
              "Duration",
              "Days Left",
              "Delay Penalty",
              "RA Basic",
              "Approved RA",
              "Paid",
              "Outstanding",
              "Payment",
              "GST Invoice",
              "Status",
              "RA / GST",
              "Action",
            ].map((heading) => (
              <th
                key={heading}
                className="border border-slate-600 px-1.5 py-3 text-center font-extrabold uppercase leading-3 tracking-[0.03em]"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredProjects.map((project, index) => {
            const status =
              project.progress >= 95
                ? "Completed"
                : project.progress > 0
                ? "In Progress"
                : "Not Started";
            const statusBadge =
              project.progress >= 95
                ? "green"
                : project.progress > 0
                ? "blue"
                : "gray";
            const countdown = getCountdown(project);
            const delayPenalty = getDelayPenalty(project);
            const paymentStatus =
              project.paymentOutstandingTotal <= 0 && project.paymentPaidTotal > 0
                ? "FULLY_PAID"
                : project.paymentPaidTotal > 0
                ? "PART_PAID"
                : "PENDING";
            const cell =
              "border border-slate-300 px-1.5 py-3 align-middle break-words leading-3";

            return (
              <tr key={project.id} className="even:bg-slate-50 hover:bg-emerald-50">
                <td className={`${cell} text-center font-extrabold`}>{index + 1}</td>
                <td className={`${cell} align-top`}>
                  <div className="font-extrabold text-slate-950">{project.projectTitle}</div>
                  <div className="mt-1 break-all font-mono text-[7px] text-slate-500">
                    ID: {project.id}
                  </div>
                </td>
                <td className={`${cell} align-top font-bold`}>{project.pacsName}</td>
                <td className={`${cell} align-top text-center`}>{project.district}</td>
                <td className={cell}><ProgressBar value={project.progress} /></td>
                <td className={`${cell} text-right font-extrabold text-slate-950`}>
                  {formatINR(project.awardAmount)}
                </td>
                <td className={`${cell} text-center font-bold`}>
                  {project.workDurationDays ? `${project.workDurationDays} Days` : "—"}
                </td>
                <td className={`${cell} text-center font-extrabold`}>
                  <div className={countdown.color}>
                    {countdown.daysLeft === "—" ? "—" : `${countdown.daysLeft} Days`}
                    <div className="mt-0.5 text-[7px]">{countdown.label}</div>
                  </div>
                </td>
                <td className={`${cell} text-right font-extrabold`}>
                  {delayPenalty.penalty > 0 ? (
                    <div className="text-red-600">
                      {formatINR(delayPenalty.penalty)}
                      <div className="mt-0.5 text-[7px]">
                        {delayPenalty.overdueDays} days delay
                      </div>
                    </div>
                  ) : (
                    <span className="text-emerald-700">No Penalty</span>
                  )}
                </td>
                <td className={`${cell} text-right font-extrabold`}>
                  {formatINR(project.raTotal)}
                  <div className="mt-0.5 text-[7px] text-slate-500">
                    {project.raCount} bills
                  </div>
                </td>
                <td className={`${cell} text-right font-extrabold text-emerald-700`}>
                  {formatINR(project.raApprovedTotal)}
                  <div className="mt-0.5 text-[7px] text-slate-500">
                    {project.raApproved} approved
                  </div>
                </td>
                <td className={`${cell} text-right font-extrabold text-emerald-700`}>
                  {formatINR(project.paymentPaidTotal)}
                  <div className="mt-0.5 text-[7px] text-slate-500">
                    {project.fullyPaidCount} full / {project.partPaidCount} part
                  </div>
                </td>
                <td className={`${cell} text-right font-extrabold text-red-600`}>
                  {formatINR(project.paymentOutstandingTotal)}
                  <div className="mt-0.5 text-[7px] text-slate-500">
                    {project.paymentPendingCount} pending
                  </div>
                </td>
                <td className={`${cell} text-center`}>
                  <Badge type={paymentBadgeType(paymentStatus)}>{paymentStatus}</Badge>
                </td>
                <td className={`${cell} text-center font-extrabold`}>
                  <div className="text-emerald-700">{project.gstInvoiceUploaded} Uploaded</div>
                  <div className="mt-0.5 text-[7px] text-red-600">
                    {project.gstInvoicePending} Pending
                  </div>
                </td>
                <td className={`${cell} text-center`}>
                  <Badge type={statusBadge}>{status}</Badge>
                </td>
                <td className={`${cell} text-center`}>
                  <button
                    type="button"
                    onClick={() => setSelectedRaProject(project)}
                    className="w-full rounded-md border border-blue-200 bg-blue-50 px-1 py-2 text-[8px] font-extrabold text-blue-800 hover:bg-blue-100"
                  >
                    View RA
                  </button>
                </td>
                <td className={`${cell} text-center`}>
                  <button
                    type="button"
                    onClick={() => openProject(project)}
                    className="w-full rounded-md bg-emerald-700 px-1 py-2 text-[8px] font-extrabold text-white hover:bg-emerald-800"
                  >
                    {sectionInfo.button}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderProjectListing = () => (
    <>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[260px_minmax(0,1fr)_auto] md:items-end">
          <div>
            <label className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
              Project Status
            </label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="all">All Projects</option>
              <option value="notStarted">Not Started</option>
              <option value="inProgress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="raPending">RA Pending</option>
              <option value="gstPending">GST Invoice Pending</option>
              <option value="paymentPending">Payment Outstanding</option>
              <option value="partPaid">Part Paid</option>
              <option value="fullyPaid">Fully Paid</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
              Search Project
            </label>
            <input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search project, PACS, district or tender ID..."
              className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 px-3 text-xs font-semibold text-slate-800 outline-none placeholder:font-medium placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <button
            type="button"
            onClick={() => {
              setSearchText("");
              setStatusFilter("all");
            }}
            className="h-10 rounded-lg border border-slate-300 bg-slate-50 px-5 text-[10px] font-extrabold uppercase tracking-wider text-slate-700 hover:bg-slate-100"
          >
            Clear Filter
          </button>
        </div>
      </div>

      <section className="mt-4 overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-slate-300 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-extrabold text-slate-950">Project-wise Details</h2>
            <p className="mt-1 text-[10px] font-medium text-slate-500">
              Bordered desktop table; readable card layout on smaller screens.
            </p>
          </div>
          <span className="inline-flex w-fit rounded-md bg-slate-100 px-3 py-1.5 text-[10px] font-extrabold text-slate-700">
            {filteredProjects.length} of {projects.length} projects
          </span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-xs font-semibold text-slate-500">
            Loading projects...
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="py-16 text-center text-xs font-semibold text-slate-500">
            No project found for the selected filter.
          </div>
        ) : (
          <>
            {renderDesktopProjectTable()}
            <div className="grid gap-4 p-4 md:grid-cols-2 2xl:hidden">
              {filteredProjects.map((project, index) => (
                <ProjectMobileCard
                  key={project.id}
                  project={project}
                  index={index}
                  sectionInfo={sectionInfo}
                  onViewRa={setSelectedRaProject}
                  onOpen={openProject}
                />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );

  const renderRaDetails = () => {
    if (!selectedRaProject) return null;
    const bills = selectedRaProject.raBillList || [];

    return (
      <div className="fixed inset-0 z-[100] flex min-h-0 max-w-full flex-col overflow-x-hidden bg-slate-100">
        <div className="h-1 shrink-0 bg-gradient-to-r from-orange-500 via-white to-emerald-600" />
        <div className="flex shrink-0 flex-col gap-3 border-b border-slate-300 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between lg:px-6">
          <div className="min-w-0">
            <div className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">
              Contractor Portal · RA / GST Detail
            </div>
            <h2 className="mt-1 break-words text-lg font-extrabold text-slate-950">
              {selectedRaProject.projectTitle}
            </h2>
            <p className="mt-1 text-[10px] font-medium text-slate-500">
              GST invoice, accounting status, payment voucher and outstanding details.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSelectedRaProject(null)}
            className="shrink-0 rounded-lg bg-slate-900 px-5 py-2.5 text-[10px] font-extrabold uppercase tracking-wider text-white hover:bg-slate-800"
          >
            ← Back to Projects
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 lg:p-6">
          {bills.length === 0 ? (
            <div className="rounded-xl border border-slate-300 bg-white py-16 text-center text-xs font-semibold text-slate-500">
              No RA bills found for this project.
            </div>
          ) : (
            <>
              <div className="hidden max-w-full overflow-hidden rounded-xl border border-slate-300 bg-white 2xl:block">
                <table className="w-full table-fixed border-collapse text-[9px] text-slate-700">
                  <colgroup>
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "6%" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      {[
                        "RA Bill / Invoice",
                        "Basic",
                        "GST 18%",
                        "Gross",
                        "Net Payable",
                        "Paid",
                        "Outstanding",
                        "Payment Detail",
                        "Accounting Status",
                        "Correction",
                        "GST Invoice",
                        "Upload",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="border border-slate-600 px-2 py-3 text-center font-extrabold uppercase leading-3 tracking-[0.03em]"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((bill) => {
                      const cell = "break-words border border-slate-300 px-2 py-3 align-top leading-4";
                      return (
                        <tr key={bill.id} className="even:bg-slate-50 hover:bg-emerald-50">
                          <td className={`${cell} font-extrabold text-slate-950`}>
                            {bill.raInvoiceNo || bill.billNo || bill.raBillNo || bill.id}
                            <div className="mt-1 text-[8px] font-medium text-slate-500">
                              Date: {fmtDate(bill.raInvoiceDate || bill.invoiceDate || bill.createdAt)}
                            </div>
                          </td>
                          <td className={`${cell} text-right font-bold`}>{formatINR(bill.basicAmount)}</td>
                          <td className={`${cell} text-right font-extrabold text-blue-700`}>{formatINR(bill.gstAmount)}</td>
                          <td className={`${cell} text-right font-extrabold`}>{formatINR(bill.grossWithGST)}</td>
                          <td className={`${cell} text-right font-extrabold text-emerald-700`}>{formatINR(bill.netPayable)}</td>
                          <td className={`${cell} text-right font-extrabold text-emerald-700`}>{formatINR(bill.paidAmount)}</td>
                          <td className={`${cell} text-right font-extrabold text-red-600`}>{formatINR(bill.outstandingAmount)}</td>
                          <td className={`${cell} font-bold`}>
                            <Badge type={paymentBadgeType(bill.paymentStatus)}>{bill.paymentStatus}</Badge>
                            {bill.lastPaymentVoucherNo && <div className="mt-1">Voucher: {bill.lastPaymentVoucherNo}</div>}
                            {bill.lastPaymentUtr && <div className="break-all">UTR: {bill.lastPaymentUtr}</div>}
                            {bill.lastPaymentDate && <div>Date: {fmtDate(bill.lastPaymentDate)}</div>}
                          </td>
                          <td className={`${cell} font-extrabold`}>{bill.accountingStatus || "PENDING"}</td>
                          <td className={`${cell} font-bold text-red-600`}>{bill.accountCorrectionRemark || "—"}</td>
                          <td className={`${cell} text-center`}>
                            {bill.gstInvoiceUrl ? (
                              <button
                                type="button"
                                onClick={() => window.open(bill.gstInvoiceUrl, "_blank")}
                                className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-2 text-[8px] font-extrabold text-emerald-800 hover:bg-emerald-100"
                              >
                                View Invoice
                              </button>
                            ) : (
                              <span className="font-extrabold text-red-600">Not Uploaded</span>
                            )}
                          </td>
                          <td className={`${cell} text-center`}>
                            <label className="inline-flex cursor-pointer rounded-md bg-blue-700 px-2 py-2 text-[8px] font-extrabold text-white hover:bg-blue-800">
                              {uploadingRaId === bill.id ? "Uploading..." : "Upload"}
                              <input
                                type="file"
                                accept="application/pdf,image/*"
                                className="hidden"
                                disabled={uploadingRaId === bill.id}
                                onChange={(event) => uploadGstInvoice(bill, event.target.files?.[0])}
                              />
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid min-w-0 gap-4 md:grid-cols-2 2xl:hidden">
                {bills.map((bill) => (
                  <article key={bill.id} className="min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
                    <div className="border-b border-slate-200 bg-slate-900 px-4 py-3 text-white">
                      <div className="text-xs font-extrabold">
                        {bill.raInvoiceNo || bill.billNo || bill.raBillNo || bill.id}
                      </div>
                      <div className="mt-1 text-[9px] text-slate-300">
                        Date: {fmtDate(bill.raInvoiceDate || bill.invoiceDate || bill.createdAt)}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 text-[10px]">
                      {[
                        ["Basic", formatINR(bill.basicAmount)],
                        ["GST 18%", formatINR(bill.gstAmount)],
                        ["Gross", formatINR(bill.grossWithGST)],
                        ["Net Payable", formatINR(bill.netPayable)],
                        ["Paid", formatINR(bill.paidAmount)],
                        ["Outstanding", formatINR(bill.outstandingAmount)],
                      ].map(([label, value], index) => (
                        <div key={label} className={`border-b border-slate-200 p-3 ${index % 2 === 0 ? "border-r" : ""}`}>
                          <div className="font-bold uppercase text-slate-400">{label}</div>
                          <div className="mt-1 break-words font-extrabold text-slate-800">{value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2 border-b border-slate-200 p-4 text-[10px]">
                      <div className="flex items-start justify-between gap-3">
                        <span className="font-bold text-slate-400">Payment</span>
                        <Badge type={paymentBadgeType(bill.paymentStatus)}>{bill.paymentStatus}</Badge>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <span className="font-bold text-slate-400">Accounting</span>
                        <span className="text-right font-extrabold text-slate-800">{bill.accountingStatus || "PENDING"}</span>
                      </div>
                      {bill.accountCorrectionRemark && (
                        <div className="rounded-md bg-red-50 p-2 font-bold text-red-700">{bill.accountCorrectionRemark}</div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-4">
                      {bill.gstInvoiceUrl ? (
                        <button
                          type="button"
                          onClick={() => window.open(bill.gstInvoiceUrl, "_blank")}
                          className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[10px] font-extrabold text-emerald-800"
                        >
                          View Invoice
                        </button>
                      ) : (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-center text-[10px] font-extrabold text-red-700">
                          Not Uploaded
                        </div>
                      )}
                      <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-blue-700 px-3 py-2.5 text-[10px] font-extrabold text-white">
                        {uploadingRaId === bill.id ? "Uploading..." : "Upload GST"}
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          className="hidden"
                          disabled={uploadingRaId === bill.id}
                          onChange={(event) => uploadGstInvoice(bill, event.target.files?.[0])}
                        />
                      </label>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen w-full max-w-full overflow-x-hidden bg-slate-100 text-slate-900"
      style={{
        maxWidth: "100vw",
        fontFamily:
          'Inter, "Noto Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <Toaster position="top-center" />
      <div className="h-1 bg-gradient-to-r from-orange-500 via-white to-emerald-600" />

      <div className="border-b border-white/10 bg-[#0b1f3a] px-4 py-1.5 text-white lg:px-6">
        <div className="flex items-center justify-between gap-4 text-[8px] font-bold uppercase tracking-[0.12em] sm:text-[9px]">
          <span className="truncate text-slate-200">
            Ministry Registered Multistate Cooperative Society
          </span>
          <span className="hidden shrink-0 text-emerald-300 sm:inline">
            Reg. No. MSCS/CR/1572/2024 · Secure Digital Portal
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="flex min-h-[72px] w-full min-w-0 max-w-full items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <button
            type="button"
            onClick={backToDashboard}
            className="flex min-w-0 items-center gap-3 text-left"
          >
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-[3px] border-double border-emerald-800 bg-white text-[10px] font-black tracking-tight text-emerald-900 shadow-sm">
              SAAM
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-extrabold text-[#0b1f3a] sm:text-[15px]">
                Contractor Project Management Portal
              </span>
              <span className="mt-0.5 block truncate text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-700 sm:text-[10px]">
                Shine Agri Allied & Marketing MSCS Ltd.
              </span>
              <span className="mt-0.5 hidden text-[8px] font-semibold text-slate-400 lg:block">
                Project Execution · Measurement · RA Billing · Payment Monitoring
              </span>
            </span>
          </button>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/tender/dashboard")}
              className="inline-flex rounded-lg bg-[#0b1f3a] px-3 py-2.5 text-[9px] font-extrabold text-white hover:bg-emerald-800 sm:px-4 sm:text-[10px]"
            >
              ← Vendor Dashboard
            </button>
            <button
              type="button"
              onClick={loadData}
              className="hidden rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[10px] font-extrabold text-slate-700 hover:bg-slate-50 lg:inline-flex"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="hidden rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[10px] font-extrabold text-slate-700 hover:bg-slate-50 xl:inline-flex"
            >
              Main Website
            </button>
            <button
              type="button"
              onClick={doLogout}
              className="rounded-lg bg-red-600 px-4 py-2.5 text-[10px] font-extrabold text-white hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="w-full min-w-0 max-w-full overflow-x-hidden px-3 py-5 sm:px-4 lg:px-6 lg:py-6">
        {section === "home" ? (
          <>
            <section className="overflow-hidden rounded-2xl bg-slate-900 text-white shadow-xl">
              <div className="grid gap-6 px-5 py-7 md:grid-cols-[minmax(0,1fr)_auto] md:items-center lg:px-8">
                <div className="min-w-0">
                  <div className="text-[9px] font-extrabold uppercase tracking-[0.2em] text-emerald-300">
                    Integrated Project Execution & Billing System
                  </div>
                  <h1 className="mt-2 break-words text-2xl font-extrabold leading-tight lg:text-3xl">
                    Contractor Workspace
                  </h1>
                  <p className="mt-2 max-w-3xl text-xs font-medium leading-5 text-slate-300">
                    Authorized workspace for {companyName}. Manage awarded projects, site progress, measurements, RA bills, GST invoices and payments through a controlled digital workflow.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[10px] font-semibold text-slate-300">
                    <span>Vendor ID: <strong className="text-white">{vendorId || "—"}</strong></span>
                    <span>Login: <strong className="text-white">{vendorEmail || "—"}</strong></span>
                    <span className="text-emerald-300">● Authorized Contractor Access</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/15 bg-white/15 text-center">
                  <div className="bg-slate-900/80 px-6 py-4">
                    <div className="text-2xl font-extrabold">{totals.total}</div>
                    <div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">Awarded</div>
                  </div>
                  <div className="bg-slate-900/80 px-6 py-4">
                    <div className="text-2xl font-extrabold text-emerald-300">{totals.avg}%</div>
                    <div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">Progress</div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mt-5">{renderStats()}</section>

            <section className="mt-7">
              <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-lg font-extrabold text-slate-950">Contractor Services</h2>
                  <p className="mt-1 text-[11px] font-medium text-slate-500">
                    All existing modules are available here as direct-access cards.
                  </p>
                </div>
                <div className="text-[9px] font-extrabold uppercase tracking-[0.15em] text-emerald-700">
                  Secure Contractor Workspace
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {DASHBOARD_MODULES.map((module) => {
                  const metric = getModuleMetric(module.key);
                  return (
                    <DashboardModuleCard
                      key={module.key}
                      module={module}
                      value={metric.value}
                      note={metric.note}
                      onClick={() => openModule(module)}
                    />
                  );
                })}
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="mb-4 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between lg:p-5">
              <div className="flex min-w-0 items-start gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                  <ModuleIcon name={sectionInfo.icon} />
                </span>
                <div className="min-w-0">
                  <div className="text-[9px] font-extrabold uppercase tracking-[0.16em] text-emerald-700">
                    Full-width Contractor Workspace
                  </div>
                  <h1 className="mt-1 text-xl font-extrabold text-slate-950">{sectionInfo.title}</h1>
                  <p className="mt-1 text-[11px] font-medium leading-5 text-slate-500">
                    {sectionInfo.description}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={loadData}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[10px] font-extrabold text-slate-700 hover:bg-slate-50"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={backToDashboard}
                  className="rounded-lg bg-slate-900 px-4 py-2.5 text-[10px] font-extrabold text-white hover:bg-slate-800"
                >
                  ← Dashboard
                </button>
              </div>
            </section>

            <section className="mb-4">{renderStats(true)}</section>

            {section === "construction" ? (
              <section className="min-w-0 max-w-full overflow-x-hidden rounded-xl border border-slate-200 bg-white p-3 shadow-sm lg:p-5">
                <ContractorConstructionPanel />
              </section>
            ) : (
              renderProjectListing()
            )}
          </>
        )}
      </main>

      <footer className="mt-8 border-t border-slate-200 bg-white px-4 py-4 text-center text-[9px] font-semibold text-slate-400">
        Shine Agri Allied & Marketing Multistate Cooperative Society Ltd. · Reg. No. MSCS/CR/1572/2024 · Contractor Project Management Portal
      </footer>

      {renderRaDetails()}
    </div>
  );
}
